import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { Controller, FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { Alert } from '../../../../shared/lib/common/Alert';
import {
  BUTTON_TEXT_CANCEL,
  BUTTON_TEXT_SAVE,
  LABEL_NAME_OF_PERSON,
  LABEL_NICKNAME,
  LABEL_PHONE_NUMBER,
  LABEL_PHONE_TYPE,
  NOTE_TRUSTED_CONTACT_SMS_NOTICE,
  PHONE_TYPE_CELL,
  PHONE_TYPE_LANDLINE,
  PLACEHOLDER_EMAIL_OPTIONAL,
} from '../../../../shared/lib/constants';
import { MSG_CONTACT_CREATED } from '../../app/constants';
import { CustomController } from '../../../../shared/lib/common/CustomController';
import { CustomPhoneController } from '../common/CustomPhoneController';
import palette from '../../../../shared/lib/theme/palette';
import { useAppContext } from '../../app/contexts/AppContext';
import { AddContactsProps } from '../../app/interfaceTypes';
import { ActionType } from '../../app/reducer/AppContextReducer';
import { createContactValidationSchema } from '../../app/validationSchema';
import { CreateContactInput, PhoneType, useCreateContactMutation } from '../../generated';
import { CustomModal } from '../common/CustomModal';
import { flexCenter } from '../../../../shared/lib/theme/styleConstants';
import ErrorIcon from '@mui/icons-material/Error';
import AddContactMessageDialog from './AddContactMessageDialog';

export const AddContact: FC<AddContactsProps> = ({ refetch }) => {
  const [open, setOpen] = useState<boolean>(false);
  const { isOpen, dispatch, contactCount, selectedPlan, cardsData } = useAppContext();
  const { borderLight } = palette;
  const { metadata } = selectedPlan || {};
  const { contacts } = metadata || {};
  const updatedContactsValue = parseInt(contacts);
  const cardsValueUpdated = isNaN(updatedContactsValue) ? contactCount : updatedContactsValue;
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const methods = useForm<CreateContactInput, any, CreateContactInput>({
    mode: 'all',

    // @ts-expect-error
    resolver: yupResolver(createContactValidationSchema),

    defaultValues: {
      name: '',
      nickName: '',
      phoneNumber: '',
      phoneType: PhoneType.Cell,
      email: ''
    }
  });

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    trigger,
    formState: { isValid, isSubmitting }
  } = methods;
  const [showSmsWarning, setShowSmsWarning] = useState<boolean>(watch('phoneType') === PhoneType.Cell);

  const handleClose = () => {
    dispatch({ type: ActionType.SET_IS_OPEN, isOpen: false });
    reset();
  };

  const [createContact, { loading }] = useCreateContactMutation({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { createContact } = data ?? {};
      if (createContact) {
        reset();
        if (cardsData?.length === 0) {
          setOpen(true);
        } else {
          Alert.success(MSG_CONTACT_CREATED);
          refetch();
        }
      }
      handleClose();
    }
  });

  const onSubmit: SubmitHandler<CreateContactInput> = async (data) => {
    const { name, nickName, phoneNumber, phoneType, email } = data ?? {};

    setPhoneNumber(phoneNumber.startsWith('+') ? `${phoneNumber}` : `+${phoneNumber}`);
    await createContact({
      variables: {
        createContactInput: {
          name,
          nickName,
          phoneNumber: phoneNumber.startsWith('+') ? `${phoneNumber}` : `+${phoneNumber}`,
          phoneType,
          contactLimit: cardsValueUpdated,
          email: email ?? undefined
        }
      }
    });
  };

  const onClose = () => {
    refetch();
    setOpen(false);
  };

  return (
    <>
      <CustomModal maxWidth="500px" isOpen={isOpen} handleClose={handleClose}>
        <Box padding="0 8px">
          <Typography variant="h6" sx={{ fontWeight: '700', color: 'blackOne', padding: '24px 0' }}>
            Add contact
          </Typography>

          <FormProvider {...methods}>
            <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3} rowSpacing={0.5}>
                <Grid item xs={12}>
                  <CustomController controllerLabel={LABEL_NICKNAME} controllerName="nickName" />
                </Grid>

                <Grid item xs={12}>
                  <CustomController controllerLabel={LABEL_NAME_OF_PERSON} controllerName="name" />
                </Grid>

                <Grid item xs={12}>
                  <CustomController controllerLabel={PLACEHOLDER_EMAIL_OPTIONAL} controllerName="email" />
                </Grid>

                <Grid item md={4} xs={12}>
                  <FormControl sx={{ marginTop: '8px' }} fullWidth>
                    <InputLabel id="demo-simple-select-label">{LABEL_PHONE_TYPE}</InputLabel>

                    <Controller
                      name="phoneType"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          defaultValue={PhoneType.Cell}
                          label={LABEL_PHONE_TYPE}
                          onChange={async (event) => {
                            const {
                              target: { value }
                            } = event;
                            field.onChange(value);
                            if (value === PhoneType.Cell) {
                              setValue('email', watch('email'));
                              await trigger(['email']);
                            } else {
                              setValue('email', watch('email'), { shouldValidate: true });
                              await trigger(['email']);
                            }
                            setShowSmsWarning(value === PhoneType.Cell);
                          }}
                        >
                          <MenuItem value={PhoneType.Cell}>{PHONE_TYPE_CELL}</MenuItem>
                          <MenuItem value={PhoneType.LandLine}>{PHONE_TYPE_LANDLINE}</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid item md={8} xs={12}>
                  <CustomPhoneController controllerLabel={LABEL_PHONE_NUMBER} controllerName="phoneNumber" />
                </Grid>
              </Grid>

              {showSmsWarning && (
                <Box>
                  <Typography sx={flexCenter} variant="h2" color="primary" mt="8px">
                    <ErrorIcon sx={{ mr: '5px' }} /> {NOTE_TRUSTED_CONTACT_SMS_NOTICE}
                  </Typography>
                </Box>
              )}

              <Box display="flex" width="100%" gap="10px" mt="24px" mb="8px">
                <Button fullWidth onClick={handleClose} variant="text" sx={{ border: `1px solid ${borderLight}` }}>
                  {BUTTON_TEXT_CANCEL}
                </Button>
                <Button fullWidth type="submit" variant="contained" color="primary" disabled={loading || !isValid || isSubmitting} endIcon={loading && <CircularProgress size={20} color="inherit" />}>
                  {BUTTON_TEXT_SAVE}
                </Button>
              </Box>
            </form>
          </FormProvider>
        </Box>
      </CustomModal>
      <AddContactMessageDialog open={open} onClose={onClose} phoneNo={phoneNumber ?? ''} />
    </>
  );
};
